var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var PaymentDetailsFormModel = require('./../payment/PaymentDetailsFormModel');
var receiptDetailsTemplate = require('./receiptDetailsTemplate.hbs');
var applicationUtils = require('./../applicationUtils');
var dateUtils = require('./../utils/dateUtils');

var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');
var InsuranceEstimationDataBehavior = require('./../estimate/InsuranceEstimationDataBehavior');
var UseModalBehavior = require('./../widget/modal/UseModalBehavior');
var RefundModalView = require('./RefundModalView');
var userService = require('./../user/userService');
var AppointmentTypeDataBehavior = require('./../appointment-type/AppointmentTypeDataBehavior');

var PAYMENT_METHODS = PaymentDetailsFormModel.PAYMENT_METHODS;
var CARDS = PaymentDetailsFormModel.CARDS;

var PAYMENT_MODALITY = {
  KIOSK: 'KIOSK',
  ADMIN_TOOL: 'ADMIN_TOOL',
  PATIENT_PORTAL: 'PATIENT_PORTAL',
  EREG: 'EREG'
};

var IS_ON_ADMIN_TOOL = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL;

require('./../i18n/i18nInit');

var withSidebarClassNameModifier = '';

if (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) {
  withSidebarClassNameModifier = ' brec-tab__content--side';
}

/**
 * Customizations to the SecureMessageWidget specific to the receipt details page.
 * @class ReceiptDetailsWidget
 * @constructor
 * @extends TorsoView
 */
module.exports = TorsoView.extend({
  tagName: 'section',
  className: 'brec-tab__content' + withSidebarClassNameModifier,
  id: 'tab-content',
  template: receiptDetailsTemplate,

  prepareFields: {
    currentApplication: applicationUtils.currentApplication,
    patientVecnaId: '_patientVecnaId',
    PAYMENT_MODALITY: PAYMENT_MODALITY,
    isOnAdminTool: IS_ON_ADMIN_TOOL
  },

  events: {
    'click [data-js-button-refund]': '_refundConfirmation'
  },

  behaviors: {
    useModalBehavior: UseModalBehavior,
    receipt: {
      behavior: SecureMessageDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      ids: function(cache) {
        var criteria = {
          isPatient: true,
          vecnaIds: [this.view._patientVecnaId],
          receiptCriteria: {
            messageVecnaIds: [this.view._receiptVecnaId]
          }
        };
        return cache.fetchIdsByCriteria(criteria);
      }
    },
    refunds: {
      behavior: SecureMessageDataBehavior,
      renderOnFetch: true,
      updateEvents: ['behaviors.receipt.data:change:refundReceiptVecnaIds', 'behaviors.receipt:fetched:ids'],
      ids: function(cache) {
        var receipt = this.view.getBehavior('receipt').data.getModel();
        if (!receipt) {
          return { skipObjectRetrieval: true };
        }
        var refundReceiptVecnaIds = receipt.get('refundReceiptVecnaIds');
        if (_.isEmpty(refundReceiptVecnaIds)) {
          return []; // clear out the ids if there are no message vecna ids to search for.
        }
        var criteria = {
          isPatient: true,
          vecnaIds: [this.view._patientVecnaId],
          receiptCriteria: {
            messageVecnaIds: refundReceiptVecnaIds
          }
        };
        return cache.fetchIdsByCriteria(criteria);
      }
    },
    billSecureMessages: {
      behavior: SecureMessageDataBehavior,
      renderOnFetch: true,
      updateEvents: ['behaviors.receipt.data:change:billMessageRetrievers.id', 'behaviors.receipt:fetched:ids'],
      ids: function() {
        var receipt = this.view.getBehavior('receipt').data.getModel();
        if (!receipt) {
          return { skipObjectRetrieval: true };
        }

        return _.pluck(receipt.get('billMessageRetrievers'), 'id');
      }
    },
    insuranceEstimation: {
      behavior: InsuranceEstimationDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      updateEvents: ['behaviors.receipt.data:change:payment.paymentDetails.account', 'behaviors.receipt:fetched:ids'],
      ids: function() {
        var receipt = this.view.getBehavior('receipt').data.getModel();
        if (!receipt) {
          return { skipObjectRetrieval: true };
        }

        var paymentDetails = receipt.get('payment.paymentDetails');
        return this.view._getAccountNumberForEstimatedCostPayment(paymentDetails);
      }
    },
    appointmentSecureMessages: {
      behavior: SecureMessageDataBehavior,
      renderOnFetch: true,
      updateEvents: ['behaviors.receipt.data:change:payment.paymentDetails.account', 'behaviors.receipt:fetched:ids'],
      ids: function(cache) {
        var receipt = this.view.getBehavior('receipt').data.getModel();
        if (!receipt) {
          return { skipObjectRetrieval: true };
        }

        var paymentDetails = receipt.get('payment.paymentDetails');
        var accountExternalId = this.view._getAccountNumberForEstimatedCostPayment(paymentDetails);

        if (!accountExternalId) {
          return { skipObjectRetrieval: true };
        }

        var criteria = {
          isPatient: true,
          vecnaIds: [this.view._patientVecnaId],
          appointmentCriteria: {
            externalAppointmentCriteria: {
              accountNumbers: [accountExternalId]
            }
          }
        };
        return cache.fetchIdsByCriteria(criteria);
      }
    },
    appointmentTypes: {
      behavior: AppointmentTypeDataBehavior,
      renderOnFetch: true,
      ids: { property: 'behaviors.appointmentSecureMessages.data:appointmentDTO.externalAppointment.type'}
    }
  },

  /**
   * Re-fetches the data used by this view.
   */
  fetchContent: function() {
    this.getBehavior('receipt').fetch();
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} the options of this widget.
   *   @param options.patientVecnaId {String} the id of the patient who's receipt this widget is displaying.
   *   @param options.receiptVecnaId {String} the id of the receipt that this widget is displaying.
   */
  initialize: function(options) {
    _.bindAll(this, '_fetch', 'render');
    this._patientVecnaId = options.patientVecnaId;
    this._receiptVecnaId = options.receiptVecnaId;

    this.listenTo(this.getBehavior('receipt'), 'fetched', this._markReceiptAsRead);
    userService.fetchUserModel()
      .then(this.render);
  },

  /**
   * @method preprender
   * @override
   */
  prerender: function() {
    this.set('billPayments', this._getCombinedPaymentAndBillDetails());

    var receiptBehavior = this.getBehavior('receipt');

    var doneLoadingReceipt = !receiptBehavior.isLoading();
    var doneLoadingBills = !this.getBehavior('billSecureMessages').isLoading();
    var doneLoadingInsuranceEstimation = !this.getBehavior('insuranceEstimation').isLoading();
    var doneLoadingAppointments = !this.getBehavior('appointmentSecureMessages').isLoading();
    var doneLoadingEstimatesInformation = doneLoadingInsuranceEstimation && doneLoadingAppointments;
    this.set('doneLoadingReceiptPayments', doneLoadingReceipt && doneLoadingBills && doneLoadingEstimatesInformation);

    var payment = _.clone(receiptBehavior.data.get('payment'));
    this.set('payment', payment);

    if (payment) {
      payment = _.extend(payment, {
        CARDS: CARDS,
        PAYMENT_METHODS: PAYMENT_METHODS,
        currentApplication: applicationUtils.currentApplication,
        expirationYear: dateUtils.getExpirationYear(payment.expirationDate),
        expirationMonth: dateUtils.getExpirationMonth(payment.expirationDate),
        cardNumberLast4: payment.last4
      });

      var totalPayment = this._getTotalPayment();
      this.set('totalPayment', totalPayment);
      var canRefund = (totalPayment > 0) && (receiptBehavior.data.get('paymentModality') !== PAYMENT_MODALITY.KIOSK);
      this.set('canRefund', canRefund);
    }
    this.set('payment', payment);

    var showRefundButton = (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) && userService.hasRole('kiosk.admin.manage.refund');
    this.set('showRefundButton', showRefundButton);
  },

  /**
   * The sum of the original receipt and all refunds.
   * @method _getTotalPayment
   * @private
   */
  _getTotalPayment: function() {
    var receiptBehavior = this.getBehavior('receipt').data;
    var totalPayment = receiptBehavior.get('payment.amount') || 0;
    var refunds = this.getBehavior('refunds').data.toJSON();
    if (refunds) {
      totalPayment = _.reduce(refunds, function (tempTotalPayment, refund) {
        return tempTotalPayment + refund.payment.amount;
      }, totalPayment);
    }
    return totalPayment;
  },

  /**
   * Looks thru the paymentDetails and if there is a payment for an estimated cost, returns the associated account number
   * @method _getAccountNumberForEstimatedCostPayment
   * @param paymentDetails {Array} the payment details for the receipt.
   * @returns {Object} account number (if available)
   * @private
   */
  _getAccountNumberForEstimatedCostPayment: function(paymentDetails) {
    var paymentDetail = _.find(paymentDetails, function(paymentDetail) {return paymentDetail.estimated;});
    return paymentDetail ? paymentDetail.account : undefined;
  },

  /**
   * Combines the paymentDetails and billList and estimated cost information into one array that contains all the needed bill-specific info
   * @returns {Array} of billpayments
   * @private
   */
  _getCombinedPaymentAndBillDetails: function() {
    var billMap = {};
    var billPayments = [];

    var paymentDetails = this.getBehavior('receipt').data.get('payment.paymentDetails');
    if (paymentDetails) {
      this.getBehavior('billSecureMessages').data.privateCollection.each(function(bill) {
        var billExternalId = bill.get('billDTO.externalBill.externalId');
        billMap[billExternalId] = bill;
      });

      billPayments = _.map(paymentDetails, function(billPayment) {
        billPayment = _.clone(billPayment);
        var billExternalId = billPayment.billExternalId;
        var bill = billMap[billExternalId];

        if (bill) {
          billPayment.paymentType = 'bill';
          billPayment.servicesId = bill.get('servicesId');
          billPayment.billExternalId = bill.get('billDTO.externalBill.externalId');
          billPayment.serviceDate = bill.get('billDTO.externalBill.serviceDate');

          var charges = bill.get('billDTO.externalBill.charges');
          billPayment.listOfServiceNames = _.pluck(charges, 'serviceName');
        } else {
          var insuranceEstimation = this.getBehavior('insuranceEstimation').data.getModel();
          billPayment.copayOnly = insuranceEstimation && insuranceEstimation.get('copayOnly');
          //must be dealing with a paid estimated cost, but make sure by checking the account number
          if (insuranceEstimation && insuranceEstimation.get('accountNumber') === billPayment.account) {
            billPayment.paymentType = 'estimatedCost';
            billPayment.estimationDate = insuranceEstimation.get('insuranceEstimation.lastUpdated');

            billPayment.listOfServiceNames = this.getBehavior('appointmentTypes').data.get('displayName');
          } else {
            //if we're here we probably can't get to the estimation service, but we should show *something*, so flag it for the template
            billPayment.paymentType = 'unknown';
          }
        }
        return billPayment;
      }, this);
    }
    return billPayments;
  },

  /**
   * Confirmation Modal to Refund (credit or void) the total amount on the current receipt.
   * @method _refundConfirmation
   * @private
   */
  _refundConfirmation: function() {
    this.showModal(this._getOrCreateRefundModal(), this._fetch, this._fetch);
  },

  /**
   * Re-fetch the data in this view (specifically force a refresh of the receipt data).
   * Also scroll to the top of the page to display the fetched data.
   * Note: Only call this if you want the page to scroll to top.
   * @method _fetch
   * @private
   */
  _fetch: function() {
    this.getBehavior('receipt').fetch();
    if(!_.isUndefined(window) && !_.isUndefined(window.top)) {
      window.top.scrollTo(0, 0);
    }
  },

  /**
   * Always refund entire payment.
   * @method _getRefundAmount
   * @private
   */
  _getRefundAmount: function() {
    return this._getTotalPayment();
  },

  /**
   * Lazy-create the modal view for refunding the payment.
   * If the modal view hasn't been created, then create it.  Either way return a created modal view.
   * @method _getOrCreateRefundModal
   * @return {ModalDialogWidget}
   * @private
   */
  _getOrCreateRefundModal: function() {
    var refundAmount = this._getRefundAmount();
    var receiptMessageVecnaId = this.getBehavior('receipt').data.get('vecnaId');
    if (!this._confirmRefundModal) {
      // Initialize with the refund amount to avoid any issues with the initial render.
      this._confirmRefundModal = new RefundModalView({
        dataViewName: 'refund-confirm-modal',
        refundAmount: refundAmount,
        receiptMessageVecnaId: receiptMessageVecnaId,
        patientVecnaId: this._patientVecnaId
      });
    }
    // Update the refund amount in case it has changed since the modal was last displayed (no change will not cause a re-render).
    this._confirmRefundModal.reset(refundAmount);
    return this._confirmRefundModal;
  },

  /**
   * Mark this receipt as read
   * @method _markReceiptAsRead
   */
  _markReceiptAsRead: function() {
    var receipts = this.getBehavior('receipt').data.getModels();
    _.each(receipts, function(receipt) {
        receipt.markAsRead();
    });
    userService.messageCountsModel.fetch();
  }
});
